import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Box, TextField, useTheme } from '@mui/material';
import React, { useCallback } from 'react';

import { ISelectorValue } from '../../../types/shell';

interface ISelectorProps<T> {
  value: T | undefined | null;
  options: T[] | undefined;
  label?: string;
  onChange?: (value: T | null) => void;
  name?: string;
  required?: boolean;
  error?: string | string[] | null;
  dataAction?: string;
  autoComplete?: string;
}

export const Selector = <T extends ISelectorValue>({
  options,
  label,
  value,
  onChange,
  name,
  required = false,
  error,
  dataAction,
  autoComplete,
}: ISelectorProps<T>) => {
  const theme = useTheme();

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: T | null) => {
      onChange?.(newValue);
    },
    [onChange],
  );

  // Detect browser autofill
  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      if (reason === 'reset' || reason === 'input') {
        const matchedOption = options?.find((opt) => opt.value === newInputValue || opt.label === newInputValue);
        if (matchedOption && matchedOption.value !== value?.value) {
          onChange?.(matchedOption as T | null);
        }
      }
    },
    [onChange, options, value],
  );

  return (
    <Box data-action={dataAction}>
      <Autocomplete
        options={options || []}
        value={value || null}
        onChange={handleChange}
        onInputChange={handleInputChange}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        popupIcon={<SearchIcon />}
        openOnFocus={false}
        sx={{
          '& .MuiAutocomplete-popupIndicator': {
            transform: 'none !important',
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            label={label}
            required={required}
            error={!!error}
            helperText={error}
            autoComplete={autoComplete}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: params.InputProps.endAdornment,
              },
            }}
            sx={{
              '& .MuiInputLabel-asterisk': { color: 'error.main' },
              '& .MuiInputLabel-root': {
                color: theme.sass.ebony,
              },
              '& .MuiFormHelperText-root': {
                position: 'absolute',
                bottom: -20,
                left: 0,
                margin: 0,
              },
            }}
          />
        )}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 200,
            },
          },
        }}
      />
    </Box>
  );
};
